// SHOP DRAWINGS FROM PROCORE / ACC (owner 2026-10-25: Project at a Glance and Doc Control
// download shop drawings from each account's SHOP DRAWINGS DOWNLOAD folder). The button opens
// what those folders hold beside what is filed, and "Bring in" files the new and changed ones
// in Document Control (back/utils/shopDrawingPull). With no folder set it says so and offers
// to set one, rather than showing an empty list.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Spin, Tag } from "antd";
import { CloudDownloadOutlined } from "@ant-design/icons";
import addNotification from "helpers/notify";
import { getRemoteShopDrawings, pullRemoteShopDrawings } from "./folderPathsApi";
import { openFolderPaths } from "./FolderPathsPanel";

const NAME = { acc: "ACC", procore: "Procore" };
const STATE = {
  new: ["blue", "New"],
  changed: ["orange", "New version"],
  filed: ["green", "Filed"],
  unchanged: ["green", "Filed (same file)"],
  queued: ["default", "Queued"],
  downloading: ["default", "Downloading"],
  failed: ["red", "Failed"],
};

export function RemoteShopDrawingsList({ projectId, projectTitle, onFiled }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getRemoteShopDrawings(projectId));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, [projectId]);
  useEffect(() => {
    load();
  }, [load]);

  const pull = async () => {
    setBusy(true);
    try {
      const r = await pullRemoteShopDrawings(projectId);
      if (r.needsPath) addNotification(r.message, "warning");
      else addNotification(r.queued.length ? `${r.queued.length} shop drawing${r.queued.length === 1 ? "" : "s"} coming in - they appear in Document Control as they are filed` : "Nothing new in the shop drawings download folders", r.queued.length ? "success" : "info");
      await load();
      if (onFiled) onFiled(r);
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="text-danger">{error}</p>;
  if (!data) return <Spin />;
  if (data.needsPath) {
    return (
      <div data-testid="remote-sd-needs-path">
        <p>{data.message}</p>
        <button className="header-btn text-white bg-bid border-bid px-3" onClick={() => openFolderPaths(projectId, projectTitle)}>
          Set the shop drawings download folder
        </button>
      </div>
    );
  }
  const waiting = (data.files || []).filter((f) => ["new", "changed", "failed"].includes(f.state)).length;
  return (
    <div data-testid="remote-sd-list">
      {(data.folders || []).map((f) => (
        <div key={f.destination} className="integration-row">
          <b>{NAME[f.provider]}{f.name && f.name !== NAME[f.provider] ? ` (${f.name})` : ""}:</b>
          <span className="integration-path">{f.path}</span>
          <small className="text-muted">{f.from === "company" ? "company default" : "this project"}</small>
          {f.error ? <span className="text-danger">{f.error}</span> : <small className="text-muted">{f.count} file{f.count === 1 ? "" : "s"}</small>}
        </div>
      ))}
      <div className="folder-paths-files">
        {(data.files || []).length ? (
          data.files.map((f) => (
            <div key={f.key} className="integration-row">
              <Tag color={(STATE[f.state] || [])[0]}>{(STATE[f.state] || [])[1] || f.state}</Tag>
              <span title={f.path}>{f.name}</span>
              {f.error ? <small className="text-danger">{f.error}</small> : null}
            </div>
          ))
        ) : (
          <p className="text-muted">The folders hold no drawings yet.</p>
        )}
      </div>
      <div className="d-flex mt-2" style={{ gap: 8 }}>
        <button className="header-btn text-white bg-bid border-bid px-3" disabled={busy || !waiting} onClick={pull} data-testid="remote-sd-pull">
          {busy ? "Bringing in..." : `Bring in ${waiting || ""} new`.replace("  ", " ")}
        </button>
        <button className="bg-white text-bid border-bid shadow-none px-3" disabled={busy} onClick={load}>
          Refresh
        </button>
        <button className="integration-link-btn" onClick={() => openFolderPaths(projectId, projectTitle)}>
          Change folders
        </button>
      </div>
    </div>
  );
}

export default function RemoteShopDrawingsButton({ projectId, projectTitle, onFiled, size = "small", className, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size={size} className={className} icon={<CloudDownloadOutlined />} disabled={disabled || !projectId} onClick={() => setOpen(true)}
              title="Bring shop drawings in from the project's Procore / ACC shop drawings download folders" data-testid="remote-sd-button">
        Shop drawings from Procore / ACC
      </Button>
      {open ? (
        <Modal open title={`Shop drawings from Procore / ACC${projectTitle ? ` - ${projectTitle}` : ""}`} footer={null} width={720} onCancel={() => setOpen(false)} destroyOnClose>
          <RemoteShopDrawingsList projectId={projectId} projectTitle={projectTitle} onFiled={onFiled} />
        </Modal>
      ) : null}
    </>
  );
}
